import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutGrid, FileText, PenLine, ShieldCheck, ListChecks, Lock, Settings, Headphones,
  Bell, LogOut, ChevronDown, ShieldHalf, Sun, Moon,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { setToken } from "@/lib/api";

const NAV = [
  { key: "overview", labelKey: "nav.overview", Icon: LayoutGrid },
  { key: "documents", labelKey: "nav.documents", Icon: FileText },
  { key: "signatures", labelKey: "nav.signatures", Icon: PenLine },
  { key: "verification", labelKey: "nav.verification", Icon: ShieldCheck },
  { key: "audit", labelKey: "nav.audit", Icon: ListChecks },
  { key: "vault", labelKey: "nav.vault", Icon: Lock },
  { key: "settings", labelKey: "nav.settings", Icon: Settings },
  { key: "support", labelKey: "nav.support", Icon: Headphones },
];

export function PortalShell({ userName, userRole, active, onNavigate, title, subtitle, notifications = [], children }) {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const [theme, setTheme] = useState("light");
  useEffect(() => {
    const saved = localStorage.getItem("portal-theme");
    if (saved === "dark" || saved === "light") setTheme(saved);
  }, []);
  const toggleTheme = () =>
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("portal-theme", next);
      return next;
    });
  const isDark = theme === "dark";
  const themeClass = isDark ? "portal-dark" : "portal-light";

  const fmtNotifTime = (iso) =>
    new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-US", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin",
    });

  const toneDot = (tone) => (tone === "emerald" ? "bg-emerald-400" : tone === "amber" ? "bg-amber-400" : "bg-accent");

  const handleLogout = () => {
    setToken(null);
    navigate("/");
  };

  const initials = (userName || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className={`${themeClass} portal-bg min-h-screen text-foreground`}>
      <div className="flex min-h-screen">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar/80 backdrop-blur-md lg:flex">
          <div className="flex h-20 items-center border-b border-sidebar-border px-6">
            <Link to="/" aria-label="Aureus Notariat"><BrandLogo invert={isDark} /></Link>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-6" aria-label="Portal">
            <ul className="space-y-1">
              {NAV.map(({ key, labelKey, Icon }) => {
                const isActive = active === key;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => onNavigate(key)}
                      aria-current={isActive ? "page" : undefined}
                      className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground/65 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                      }`}
                    >
                      {isActive && <span className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-full bg-sidebar-primary" />}
                      <Icon className="h-[18px] w-[18px]" />
                      <span className="tracking-wide">{t(labelKey)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="px-4 pb-3">
            <div className="gold-ring flex items-center gap-3 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-4 py-3">
              <ShieldHalf className="h-6 w-6 text-sidebar-primary" />
              <div>
                <p className="text-xs font-semibold text-sidebar-foreground">{t("portal.enc.title")}</p>
                <p className="tech-label text-[10px] text-sidebar-foreground/55">{t("portal.enc.sub")}</p>
              </div>
            </div>
          </div>
          <div className={`h-28 w-full bg-cover bg-bottom ${isDark ? "opacity-70" : "opacity-[0.06]"}`}
               style={{ backgroundImage: "url(/sidebar-plexus.png)" }} aria-hidden="true" />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 border-b border-sidebar-border bg-sidebar/70 backdrop-blur-md">
            <div className="flex h-20 items-center justify-between gap-4 px-4 sm:px-6">
              <div className="min-w-0">
                <h1 className="truncate font-serif text-xl font-semibold tracking-wide text-foreground sm:text-2xl">{title}</h1>
                <p className="tech-label mt-0.5 truncate text-muted-foreground">{subtitle}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="hidden items-center gap-2 rounded-full border border-sidebar-primary/30 bg-sidebar-primary/10 px-3 py-1.5 text-xs font-medium text-sidebar-primary md:inline-flex">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t("portal.secure")}
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  </span>
                </span>
                <LanguageSwitcher invert={isDark} />
                <button type="button" onClick={toggleTheme} aria-label={t("portal.theme")} title={t("portal.theme")}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-sidebar-border text-sidebar-foreground/70 transition-colors hover:text-sidebar-foreground">
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="relative flex h-9 w-9 items-center justify-center rounded-full border border-sidebar-border text-sidebar-foreground/70 transition-colors hover:text-sidebar-foreground" aria-label={t("portal.notifications")}>
                      <Bell className="h-4 w-4" />
                      {notifications.length > 0 && <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-sidebar-primary" />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={`${themeClass} w-80 p-0`}>
                    <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                      <p className="text-sm font-medium text-foreground">{t("portal.notifications")}</p>
                      {notifications.length > 0 && (
                        <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-medium text-accent">{notifications.length}</span>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t("notif.empty")}</p>
                    ) : (
                      <ul className="max-h-80 overflow-y-auto py-1">
                        {notifications.map((n) => (
                          <li key={n.id} className="flex gap-3 px-4 py-2.5">
                            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${toneDot(n.tone)}`} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">{n.title}</p>
                              {n.description && <p className="truncate text-xs text-muted-foreground">{n.description}</p>}
                              <p className="mt-0.5 text-[11px] text-muted-foreground/70">{fmtNotifTime(n.at)}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button type="button" onClick={() => onNavigate("settings")} aria-label={t("portal.section.settings")}
                        className="flex items-center gap-2.5 rounded-full border border-sidebar-border bg-sidebar-accent/40 py-1 pl-1 pr-2.5 transition-colors hover:bg-sidebar-accent">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sidebar-primary/20 text-xs font-semibold text-sidebar-primary">{initials}</span>
                  <div className="hidden text-right leading-tight sm:block">
                    <p className="text-sm font-medium text-sidebar-foreground">{userName}</p>
                    <p className="text-[11px] text-sidebar-foreground/55">{userRole === "admin" ? t("portal.role.admin") : t("portal.role.client")}</p>
                  </div>
                  <ChevronDown className="hidden h-4 w-4 text-sidebar-foreground/50 sm:block" />
                </button>
                <Button variant="outline" size="sm" onClick={handleLogout}
                        className="border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">{t("portal.logout")}</span>
                </Button>
              </div>
            </div>
            <nav className="flex gap-1 overflow-x-auto border-t border-sidebar-border px-3 py-2 lg:hidden" aria-label="Portal mobil">
              {NAV.map(({ key, labelKey, Icon }) => {
                const isActive = active === key;
                return (
                  <button key={key} type="button" onClick={() => onNavigate(key)}
                          className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground/60"}`}>
                    <Icon className="h-4 w-4" />
                    {t(labelKey)}
                  </button>
                );
              })}
            </nav>
          </header>

          <main className="flex-1 px-4 py-8 sm:px-6">{children}</main>

          <footer className="border-t border-sidebar-border/70 py-8">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-2 px-4 text-center">
              <p className="font-serif text-sm uppercase tracking-[0.28em] text-foreground/90">{t("portal.footer.tagline")}</p>
              <p className="tech-label text-muted-foreground">{t("portal.footer.sub")}</p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
